import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useFocusEffect } from '@react-navigation/native';
import i18n from 'i18n-js';
import { machineStatusName } from '@helpers';
import { getNCFileById } from '@reducers/manufacture';
import { selectNcFileData, setNcFileData } from '@reducers/details';
import { selectServerOffset } from '@reducers/time';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { useManageHomePageButtonsPress } from '@components/operatorPanel/hooks/useManageHomePageButtonsPress';
import type { NCFileCentralPartProps } from '@components/operatorPanel/components/elements/NCFileCentralPart';

type UseManageNCFileCentralPartParams = Pick<
  NCFileCentralPartProps,
  'CC' | 'ncFileId' | 'machineStatus' | 'ncFileChange' | 'ncFileSetup' | 'isOperator' | 'productionBatchId' | 'scale' | 'font'
>;

export const useManageNCFileCentralPart = ({
  CC,
  ncFileId,
  machineStatus,
  ncFileChange,
  ncFileSetup,
  isOperator,
  productionBatchId,
  scale,
  font,
}: UseManageNCFileCentralPartParams) => {
  const dispatch = useAppDispatch();

  const ncFileData = useSelector(selectNcFileData);
  const offset = useSelector(selectServerOffset);

  useFocusEffect(
    useCallback(() => {
      if (ncFileId == null) return;
      (async () => {
        const ncFile = await dispatch(getNCFileById(ncFileId));
        if (ncFile) {
          dispatch(setNcFileData(ncFile));
        }
      })();
    }, [ncFileId])
  );

  const { onModifyNCFileTypePress } = useManageHomePageButtonsPress({
    CC,
    ncFileId,
    ncFileName: ncFileData?.data?.ncFileName || '',
    font,
    productionBatchId,
    scale,
  });

  const status = machineStatus.status;
  const subStatus = machineStatus.subStatus;

  const shouldDisplaySelectNCFileTypeButton = ncFileSetup && isOperator;

  const customStatusText =
    ncFileChange || ncFileSetup ? `${i18n.t(status)}` : `${i18n.t(subStatus)} ${i18n.t(machineStatusName[status as keyof typeof machineStatusName])}`;

  return {
    ncFileData,
    offset,
    onModifyNCFileTypePress,
    subStatus,
    shouldDisplaySelectNCFileTypeButton,
    customStatusText,
  };
};
